import React, { useEffect, useState } from 'react';
import { ErrorBoundary } from '@components/ErrorBoundary';
import { WelcomeExperience } from '@features/welcome/WelcomeExperience';
import { ScientificSimulationWorkspace } from '@features/scientific-simulation/ScientificSimulationWorkspace';

export type AppSurface = 'welcome' | 'simulation';

/**
 * Resolves current surface from browser URL (pathname or hash).
 * Supports both clean HTML5 paths (/simulation) and hash fallback (#/simulation).
 */
function resolveCurrentSurface(): AppSurface {
  if (typeof window === 'undefined') return 'welcome';

  const pathname = window.location.pathname.toLowerCase();
  const hash = window.location.hash.toLowerCase();

  if (
    pathname === '/simulation' ||
    pathname.startsWith('/simulation/') ||
    hash === '#/simulation' ||
    hash === '#simulation'
  ) {
    return 'simulation';
  }

  return 'welcome';
}

export const App: React.FC = () => {
  const [currentSurface, setCurrentSurface] = useState<AppSurface>(resolveCurrentSurface);

  useEffect(() => {
    const handleNavigationChange = () => {
      setCurrentSurface(resolveCurrentSurface());
    };

    window.addEventListener('popstate', handleNavigationChange);
    window.addEventListener('hashchange', handleNavigationChange);

    return () => {
      window.removeEventListener('popstate', handleNavigationChange);
      window.removeEventListener('hashchange', handleNavigationChange);
    };
  }, []);

  const navigateToSimulation = () => {
    if (typeof window !== 'undefined') {
      window.history.pushState({ surface: 'simulation' }, '', '/simulation');
      setCurrentSurface('simulation');
    }
  };

  const navigateToWelcome = () => {
    if (typeof window !== 'undefined') {
      window.history.pushState({ surface: 'welcome' }, '', '/');
      setCurrentSurface('welcome');
    }
  };

  return (
    <ErrorBoundary fallbackTitle="Application Shell Fatal Exception">
      {currentSurface === 'simulation' ? (
        /* Scientific Simulation Workspace Surface (SSW Foundation) */
        <ScientificSimulationWorkspace onHome={navigateToWelcome} />
      ) : (
        /* Final & Locked Welcome Experience Façade: ENTER leads to /simulation */
        <WelcomeExperience onEnter={navigateToSimulation} />
      )}
    </ErrorBoundary>
  );
};

export default App;
